import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { AccordionGroupMeta } from '@cudirect/shared-ui-library';
import { Info } from '@workflow/ecp-shared/constant/information.const';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { PrimitiveType } from '@workflow/class/workflow.type';
import { Acceptance, Disclosure, DisclosureApplicant } from '@workflow/ecp-workflow/dynamic-app/types/disclosure.type';
export interface DisclosuresGroup {
  eaAccordions?: { open: () => void; close: () => void }[];
}
/** component to show available accounts and services */
@BuildingBlock('disclosure')
@Component({
  selector: 'ecp-disclosure',
  styleUrls: ['./disclosure.component.scss'],
  templateUrl: './disclosure.component.html'
})
export class DisclosureComponent extends BuildingBlockControlBaseComponent implements OnInit, OnDestroy {
  /**
   * Accordion group meta data
   */
  public accordionGroupMeta = new AccordionGroupMeta();

  /**
   * Accordion disclosures
   */
  public content = '';
  public disclosures: Disclosure[] = [];
  private subs: Subscription[] = [];
  public filename: string;
  public docTitle: string;
  public isOpen = false;

  private isCallInitiated = false;
  public isDataloaded = false;
  public pdfURL = '';
  public isOlaFlow = false;
  public isOAOFlow = false;
  public isPageSubmitted = false;
  public chbx_id_prefix = 'disclosure_acc';
  public disclosure_panel_id = 'open-disclosure-panel';
  public hasPDF = false;
  // Index to track slide out panel for proper focus (ADA/WCAG)
  public panelIndex: number;
  @ViewChild('disclosuresGroup') public disclosuresGroup: DisclosuresGroup;

  /** On Init */
  public ngOnInit(): void {
    this.isOlaFlow = this.workflowService?.currentFlow?.id === 'ola' || this.workflowService?.currentFlow?.id.indexOf('ola') !== -1;
    this.isOAOFlow = this.workflowService?.currentFlow?.id === 'oao' || this.workflowService?.currentFlow?.id.indexOf('oao') !== -1;
    this.onLoadAsync(() => {
      this.disclosures = this.field?.config?.content ? JSON.parse(this.field.config.content.toString()) : [];

      let nxtIndex = -1;
      this.disclosures.forEach((element, index) => {
        // eslint-disable-next-line eqeqeq
        const anyUnCheckedApplicant = element.applicants.filter((f) => !f.accepted).length > 0;
        if (nxtIndex === -1 && anyUnCheckedApplicant) {
          nxtIndex = index;
          element.isOpen = true;
        } else {
          element.isOpen = false;
        }
        if (typeof element.allowToAccept == 'string' && element.allowToAccept.includes('api:')) {
          element.allowToAccept = this.statefulService.wrap(element.allowToAccept, undefined, index);
        }
      });
    });
    this.updateValidity();
    this.pageSubmitted$.subscribe((value) => {
      this.isPageSubmitted = value;
      if (this.isPageSubmitted) {
        this.setDisclosure();
      }
    });
  }

  /**
   * Focus 1st Invalid disclosure checkbox
   */
  // TODO: Needs some refactoring. Whati this supposed to do? You cannot focus disabled buttons/links.
  public setDisclosure(): void {
    let firstInvalidId: string;
    this.disclosures.forEach((disclosure, index) => {
      disclosure.applicants.forEach((applicant, applicantIndex) => {
        if (!applicant.accepted) {
          this.disclosuresGroup.eaAccordions.forEach((element, disIndex) => {
            if (index === disIndex) {
              // Get only the 1st invalid control id, skip other invalid id
              if (!firstInvalidId) {
                firstInvalidId = `${this.chbx_id_prefix}_${index}_${applicantIndex}`;
                this.hasPDF = disclosure.allowToAccept ? false : Boolean(disclosure.isPDFFile);
              }
              element.open();
            }
          });
        }
      });

      if (firstInvalidId) {
        setTimeout(() => {
          if (this.hasPDF) firstInvalidId = this.disclosure_panel_id + '-' + this.panelIndex;
          this.focusService.setFocus(firstInvalidId);
        }, 0);
      }
    });
  }

  public disclosureTitleHtml(disclosure: Disclosure): string {
    return `<h3 class="bl-display--flex bl-align-items--flex-start bl-width--100 bl-margin-bottom--0" aria-hidden="true">
      <i class="bl-font-size--24 bl-margin-right--16 ${this.getDisclosureSelectedStatus(disclosure)}"></i>
      <span class="bl-text-style--display-5 bl-text-align--left bl-flex--auto">
        <span class="bl-display--block">${disclosure.title}</span>
        <span class="bl-text-style--secondary-text-1"> ${this.requiredCompleted(disclosure)} </span>
      </span>
      <i class="bl-font-size--32 ${disclosure.isOpen ? 'cu-angle-up' : 'cu-angle-down'}"></i>
    </h3>`;
  }

  public getApplicantLabel(label: string): string {
    const parseToken = '#{';
    const parsedText = this.stringVariableService.parse(label, this.field.iterationId, this.field.pageIterationId, '#{')?.toString();
    if (parsedText.includes(parseToken)) {
      const left = label.indexOf(parseToken);
      const right = label.lastIndexOf('}');
      const searchStr = label.substring(left, right + 1);
      return label.replace(searchStr, 'Applicant');
    }
    return parsedText;
  }

  // Load disclosure asyn, bcze in some cases fetch disclosures from api call
  protected readonly onLoadAsync = (cb: () => void): void => {
    if (this.field?.config?.onLoad) {
      const config = this.logicService.evaluate<Promise<boolean>>(
        this.field.config.onLoad,
        {
          field: this.field,
          page: this.workflowService.currentPage
        },
        'single'
      );
      if (config?.[0]) {
        config[0].then(cb);
      } else {
        cb();
      }
    }
  };

  public getDisclosureSelectedStatus(disclosure: Disclosure): string {
    if (!disclosure.accepted && !this.allApplicantsAccepted(disclosure.applicants) && this.isPageSubmitted) {
      return 'cu-warning-solid bl-font-color--error';
    }
    return disclosure.accepted || this.allApplicantsAccepted(disclosure.applicants)
      ? 'cu-check-circle-solid bl-font-color--success'
      : 'cu-check-circle-solid bl-font-color--silver';
  }

  public requiredCompleted(disclosure: Disclosure): string {
    if (!disclosure.accepted && !this.allApplicantsAccepted(disclosure.applicants) && this.isPageSubmitted) {
      return `<span class="bl-font-color--error"> Required </span>`;
    }
    if (disclosure.accepted || this.allApplicantsAccepted(disclosure.applicants)) {
      return `<span class="bl-font-color--success"> Completed </span>`;
    }
    return '';
  }

  public updateValidity(): void {
    if (this.disclosures?.length) {
      const disclosureAccepted = this.disclosures.filter((t) => !t.accepted && !this.allApplicantsAccepted(t.applicants)).length === 0;
      this.onChange(disclosureAccepted);
    }
  }

  private allApplicantsAccepted(applicants): boolean {
    return applicants && applicants.filter((t) => !t.accepted).length === 0;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateAcceptance(applicants: Acceptance[], index: number, disclosureRef: any, disclosuresGroup: any) {
    const applicant = applicants[index];
    applicant.disabled = true;

    if (applicant.storageId) {
      this.patchValue(applicant.storageId, applicant.accepted, index);
    }

    if (applicant.agreedDateStorageId && applicant.accepted) {
      this.patchValue(applicant.agreedDateStorageId, new Date().toISOString(), index);
    }

    this.updateValidity();
    this.collapseWhenAllApplicantAccepted(applicants, disclosureRef);
    this.openIncomplete(applicants, disclosuresGroup);
    setTimeout(() => {
      applicant.disabled = false;
    }, 10);
  }

  public keepCheckboxFocus(index): void {
    // Keep selected checkbox checked/focused
    // ADA: Focus was getting lost when user toggled checkbox w/ keyboard
    const id = `${this.chbx_id_prefix}_${index}`;
    this.focusService.setFocus(id);
  }

  patchValue(storageId: string, value: PrimitiveType, iterationID: number = undefined) {
    if (storageId.includes('/$/')) {
      this.statefulService.wrap(storageId, iterationID).value = value;
    } else {
      this.statefulService.wrap(storageId).value = value;
    }
  }
  public collapseWhenAllApplicantAccepted(applicants: DisclosureApplicant[], disclosure): void {
    const isAccepted = applicants.filter((t) => !t.accepted).length === 0;
    if (isAccepted) {
      disclosure?.close();
    } else {
      disclosure?.open();
    }
    // ADA: Focus on the accordion trigger (button) when panel is collapsed
    this.focusService.setFocus(`ea-collapse-controller--${disclosure.uniqueId}`);
  }

  public openIncomplete(applicants, disclosuresGroup): void {
    const isAccepted = applicants.filter((t) => !t.accepted).length === 0;
    let nxtIndex = -1;
    if (isAccepted) {
      this.disclosures.forEach((disclosure, index) => {
        // eslint-disable-next-line eqeqeq
        if (nxtIndex == -1 && !disclosure.applicants[0].accepted) {
          disclosuresGroup.eaAccordions.forEach((element, disIndex) => {
            // eslint-disable-next-line eqeqeq
            if (nxtIndex == -1 && index == disIndex) {
              nxtIndex = index;
              element.open();
            }
          });
        }
      });
    }
  }

  /** View Final Disclosue */
  public previewDisclosure(disclosure: Disclosure): void {
    disclosure.allowToAccept = true;
    this.updateValidity();
    window.open(`/legal/${disclosure.pdfFileName}`, '_blank');
  }

  isArray(applicant): boolean {
    return Array.isArray(applicant);
  }

  protected readonly onChange = (isAccepted: boolean): void => {
    setTimeout(() => {
      this.field.data.value = isAccepted;
      if (this.field.config.onChange) {
        this.logicService.evaluate(this.field.config.onChange, {
          field: this.field,
          page: this.workflowService.currentPage
        });
      }
    }, 10);
  };

  public chkSelection(disclosure: Disclosure, disclosureRef, disclosuresGroup): void {
    if (disclosure?.isLinkType && disclosure?.acceptanceTextReview?.toLowerCase() === 'automatic') {
      if (this.isArray(disclosure.applicants)) {
        disclosure.applicants.forEach((item, i) => {
          item.accepted = true;
          this.updateAcceptance(disclosure.applicants, i, disclosureRef, disclosuresGroup);
        });
      } else {
        disclosure.accepted = true;
      }
    }

    if (typeof disclosure.allowToAccept == 'boolean') {
      disclosure.allowToAccept = true;
      return;
    }

    if (typeof disclosure.allowToAccept !== 'string') {
      disclosure.allowToAccept.value = true;
    }
  }

  public getAllowToAccept(disclosure: Disclosure): boolean {
    if (typeof disclosure?.allowToAccept == 'boolean') {
      return disclosure.allowToAccept;
    }

    if (typeof disclosure?.allowToAccept !== 'string') {
      return disclosure.allowToAccept?.value as boolean;
    }
  }

  /**
   * Check disclosure type has link
   */
  public hasLink(disclosure: Disclosure): boolean {
    return disclosure.isPDFFile || disclosure.isLinkType;
  }

  public openSlideInPanel(disclosure: Disclosure, index: number) {
    if (this.isCallInitiated) {
      return;
    }
    const disclosureFileName = disclosure.isHtmlFile ? disclosure.htmlFileName : disclosure.pdfFileName;
    const mimeType = disclosure.isHtmlFile ? Info.htmlMemeType : Info.pdfMemeType;
    this.isOpen = true;
    this.filename = `${disclosureFileName}.pdf`;
    this.docTitle = disclosure.title;
    this.isDataloaded = false;
    this.panelIndex = index;
    this.download(disclosureFileName, mimeType, disclosure.isPDFFile);
  }

  public download(value: string, mimeType: string, isPdf: boolean): Promise<void> {
    this.isCallInitiated = true;
    this.pdfURL = this.content = undefined;
    return this.globalService
      .downloadLegalDocument(value, mimeType)
      .toPromise()
      .then((blob: Blob) => {
        if (isPdf) {
          this.pdfURL = this.globalService.createBlobUrl(blob, mimeType);
        } else {
          this.apiService.blobToString(blob, (result) => {
            this.content = result;
          });
        }
        Promise.resolve(true);
        this.isDataloaded = true;
        this.isCallInitiated = false;
      })
      .catch((err) => {
        this.isCallInitiated = false;
        this.commonService.errorHandling(err);
        this.isDataloaded = true;
      });
  }

  public printPDF(): void {
    this.globalService.printPDF(this.pdfURL);
  }

  public downloadPDF(): void {
    this.globalService.downloadFilePDF(this.pdfURL, this.filename);
  }

  public closeSlideInPanel(): void {
    this.isOpen = !this.isOpen;
  }

  public ngOnDestroy(): void {
    if (this.subs && this.subs.length > 0) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
      this.subs = [];
    }
  }
}
